import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import {
    convertImage,
    fileToBase64,
    resizeImage,
    useTrackingContext,
} from '@givelify/utils';
import { GivelifyButton, GivelifyButtonProps } from '../button';
import { ConfirmModal } from '../confirmModal';
import {
    ImageEditor,
    ImageEditorApplyResult,
    ImageEditorCopy,
    UploadError,
} from '../imageEditor';

const defaultAllowedExtensions = ['png', 'jpeg', 'jpg', 'heic', 'webp', 'svg'];

export type ImageUploaderButtonProps = {
    buttonProps: GivelifyButtonProps;
    maxFileSize: number;
    onFileError?: (reason: 'size' | 'format') => void;
    // Overrides the default set of accepted file extensions (no leading
    // dot, e.g. 'png'). Defaults to the full set below when omitted, so
    // existing callers keep their current behavior.
    allowedExtensions?: string[];
    imageWidth: number;
    imageHeight: number;
    aspectRatio: number;
    avatar?: boolean;
    hideBackgroundSelection?: boolean;
    disableZoomOutBeyondCrop?: boolean;
    confirmModalName: string;
    editorName: string;
    editorCopy: ImageEditorCopy & {
        confirmModal: {
            cancelText: string;
            confirmText: string;
            description: string;
            title: string;
        };
    };
    onApply: (
        data: ImageEditorApplyResult,
        fileName: string,
    ) => void | Promise<void>;
    // Close the editor as soon as a synchronous onApply returns. Only for
    // crop-only parents that don't upload on apply; by default the editor
    // stays open until uploadSuccess turns true.
    closeOnApply?: boolean;
    onClose?: () => void;
    uploadProgress: number;
    uploadSuccess: boolean;
    uploadError?: UploadError;
    additionalTrackingParams?: Record<string, unknown>;
};
export type ImageUploaderButtonRef = {
    openFilePicker: () => void;
    openEditor: (imageBase64: string, fileName?: string) => void;
};

export const ImageUploaderButton = forwardRef<
    ImageUploaderButtonRef,
    ImageUploaderButtonProps
>(
    (
        {
            buttonProps,
            maxFileSize,
            onFileError,
            allowedExtensions = defaultAllowedExtensions,
            imageWidth,
            imageHeight,
            aspectRatio,
            avatar = false,
            hideBackgroundSelection = false,
            disableZoomOutBeyondCrop = false,
            confirmModalName,
            editorName,
            editorCopy,
            onApply,
            closeOnApply = false,
            onClose,
            uploadProgress,
            uploadSuccess,
            uploadError,
            additionalTrackingParams,
        },
        ref,
    ) => {
        const acceptedImageTypes = allowedExtensions.map(
            (extension) => `.${extension}`,
        );
        const [processedImage, setProcessedImage] = useState<string | null>();
        const [fileName, setFileName] = useState<string>('');
        const [isLoading, setIsLoading] = useState<boolean>(false);
        const [editorOpen, setEditorOpen] = useState<boolean>(false);
        const [showConfirmModal, setShowConfirmModal] =
            useState<boolean>(false);
        const [editorHidden, setEditorHidden] = useState<boolean>(false);
        const inputRef = useRef<HTMLInputElement>(null);
        const { trackEventV2 } = useTrackingContext();

        const openFilePicker = () => {
            inputRef.current?.click();
        };

        const handleButtonClick: React.MouseEventHandler<HTMLButtonElement> = (
            event,
        ) => {
            buttonProps.onClick?.(event);
            openFilePicker();
        };

        const openEditor = (imageBase64: string, name = '') => {
            if (!imageBase64) return;
            setFileName(name);
            setProcessedImage(imageBase64);
            setIsLoading(false);
            setEditorHidden(false);
            setEditorOpen(true);
        };

        useImperativeHandle(
            ref,
            () => ({
                openFilePicker,
                openEditor,
            }),
            [],
        );

        const onFileSelect: React.ChangeEventHandler<HTMLInputElement> = async (
            event,
        ) => {
            let file = event.target.files?.[0];

            if (!file) {
                return;
            }

            const extension = file.name.split('.').pop()?.toLowerCase();
            if (!extension || !allowedExtensions.includes(extension)) {
                onFileError?.('format');
                event.target.value = '';

                return;
            }

            if (file.size > maxFileSize * 1024 * 1024) {
                onFileError?.('size');
                event.target.value = '';

                return;
            }
            setFileName(file.name);
            setEditorOpen(true);
            setIsLoading(true);

            try {
                file = await convertImage(file, extension);
            } catch (e) {
                onFileError?.('format');
                setEditorOpen(false);
                setIsLoading(false);
                event.target.value = '';
                return;
            }

            file = await resizeImage(file, extension, imageWidth, imageHeight);

            const base64Image = await fileToBase64(file);
            setProcessedImage(base64Image);
            setIsLoading(false);

            event.target.value = '';
        };

        const reset = () => {
            setEditorOpen(false);
            setShowConfirmModal(false);
            setEditorHidden(false);
            setFileName('');
            setIsLoading(false);
            setProcessedImage('');
            onClose?.();
        };

        const handleClose = () => {
            if (uploadProgress > 0) {
                return;
            }
            setShowConfirmModal(true);
            setEditorHidden(true);
        };

        useEffect(() => {
            if (uploadSuccess) {
                reset();
            }
            //eslint-disable-next-line
        }, [uploadSuccess]);

        const handleEditorApply = (data: ImageEditorApplyResult) => {
            const result = onApply(data, fileName);
            const isAsyncUpload =
                result != null &&
                typeof (result as Promise<unknown>).then === 'function';

            // Crop-only parents opt in to closing immediately so repeat saves
            // work without uploadSuccess toggling false→true again. Everyone
            // else closes via the uploadSuccess effect once the upload lands.
            if (closeOnApply && !isAsyncUpload) {
                reset();
            }
        };

        return (
            <>
                <input
                    ref={inputRef}
                    hidden
                    accept={acceptedImageTypes.join(',')}
                    onChange={onFileSelect}
                    type="file"
                />
                <GivelifyButton {...buttonProps} onClick={handleButtonClick} />
                {editorOpen && (
                    <ImageEditor
                        additionalTrackingParams={additionalTrackingParams}
                        aspectRatio={aspectRatio}
                        avatar={avatar}
                        copy={editorCopy}
                        disableZoomOutBeyondCrop={disableZoomOutBeyondCrop}
                        fileName={fileName}
                        hidden={editorHidden}
                        hideBackgroundSelection={hideBackgroundSelection}
                        imageUrl={processedImage ? processedImage : ''}
                        isLoading={isLoading}
                        isUploading={uploadProgress > 0}
                        name={editorName}
                        onApply={handleEditorApply}
                        onClose={handleClose}
                        open={editorOpen}
                        outputHeight={imageHeight}
                        outputWidth={imageWidth}
                        uploadError={uploadError}
                        uploadProgress={uploadProgress}
                    />
                )}
                <ConfirmModal
                    cancelText={editorCopy.confirmModal.cancelText}
                    confirmText={editorCopy.confirmModal.confirmText}
                    description={editorCopy.confirmModal.description}
                    name={confirmModalName}
                    onCancel={() => {
                        trackEventV2?.('click', {
                            name: 'ReturnToEdit',
                            location: 'CancelCoverPhotoModal',
                            type: 'Button',
                        });
                        setShowConfirmModal(false);
                        setEditorHidden(false);
                    }}
                    onConfirm={() => {
                        trackEventV2?.('click', {
                            name: 'Cancel',
                            location: 'CancelCoverPhotoModal',
                            type: 'Button',
                        });
                        reset();
                    }}
                    open={showConfirmModal}
                    title={editorCopy.confirmModal.title}
                />
            </>
        );
    },
);

ImageUploaderButton.displayName = 'ImageUploaderButton';
